import { ChangeEvent, useState, useMemo } from "react"
import { Box, FormControl, InputLabel, MenuItem, Pagination, Paper, Select, SelectChangeEvent, useTheme } from "@mui/material"
import KitItem from "./KitItemContainer"
import { Kit, OpenModal } from "./types"
import { getDisplayItems, getShowingCount } from "./utils/helpers"
import KitHeader from "./KitHeader"
import LoopIcon from "@mui/icons-material/Loop"
import KitBuilderModal from "./KitBuilderModal"
import { selectCurrentUser } from "../../../../redux/user"
import { useAppSelector } from "../../../../redux/store"
import { KitItem as KitItemType } from "./types"
import { KitType } from "./enums"

type KitContainer = {
  kit: Kit,
  kitItemsById: Map<number, KitItemType[]>,
  type: KitType,
  customerNumber: string,
  defaultKitIcon: string,
  defaultItemIcon: string,
  fetchItems: (customerNumber: string) => Promise<void>,
}

const KitContainer = (props: KitContainer) => {
  const {
    kit,
    kitItemsById,
    type,
    customerNumber,
    defaultKitIcon,
    defaultItemIcon,
    fetchItems,
  } = props

  const theme = useTheme()

  const currentUser = useAppSelector(selectCurrentUser)

  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [isUpdatingItems, setIsUpdatingItems] = useState(false)
  const [openModal, setOpenModal] = useState<OpenModal>({ open: false, type: null})

  const kitItems = useMemo(() => {
    return kitItemsById.get(kit.id) ?? []
  }, [kit, kitItemsById])

  const displayItems = useMemo(() => {
    return getDisplayItems(kitItems)
  }, [kitItems])

  const handlePageSize = (event: SelectChangeEvent<number>) => {
    const size = Number(event.target.value)
    
    setCurrentPage(1)
    setPageSize(size)
  }

  const handlePageChange = (_: ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page)
  }

  const pagination = {
    options: [5, 10, 20, 25, 50, 100],
    start: (currentPage - 1) * pageSize,
    end: currentPage * pageSize,
    count: Math.ceil(displayItems.length / pageSize),
  }

  const pageItems = displayItems.slice(pagination.start, pagination.end)

  return (
    <>
      <Paper elevation={1} sx={{ padding: '16px 16px', marginBottom: '16px' }}>
        <KitHeader
          kit={kit}
          kitItems={kitItems}
          isUpdatingItems={isUpdatingItems}
          defaultIcon={defaultKitIcon}
          setOpenModal={setOpenModal}
          currentUser={currentUser}
        />
        {
          Boolean(kitItems.length)
            ? (
                pageItems.map(item => (
                  <KitItem
                    key={item.id}
                    kitItem={item}
                    defaultIcon={defaultItemIcon}          
                  />
                ))
              )
            : <Box>No items recorded yet for this kit</Box>
        }
        {
          isUpdatingItems
            && (
                <Box className="flex flex-align-center flex-center bold m-t-16 c-g-8">
                  <LoopIcon sx={{ color: theme.palette.success.main }} fontSize="small" />
                  {' Updating Items...'}
                </Box>
              )
        }
        {
          kitItems.length > 5
            && (
                <Box className="flex flex-space-between flex-align-center print-hide c-g-8" sx={{ paddingTop: '16px'}}>
                  <FormControl sx={{ mr: 2, minWidth: 100 }} size="small">
                    <InputLabel id="page-size-label">Page Size</InputLabel>
                    <Select<number>
                      labelId="page-size-label"
                      label="Page Size"
                      name="page-size"
                      value={pageSize}
                      onChange={handlePageSize}
                    >
                      {
                        pagination.options.map(pageSize => (
                          <MenuItem key={`${kit.id}-${pageSize}`} value={pageSize}>
                            { pageSize }
                          </MenuItem>
                        ))
                      }
                    </Select>
                  </FormControl>
                  <Box>Showing {getShowingCount(pagination.start, pagination.end, displayItems.length)} Items</Box>
                  <Pagination
                    page={currentPage}
                    count={pagination.count}
                    onChange={handlePageChange}
                    variant="outlined"
                    shape="rounded"
                  />
                </Box>                       
              )
        }
      </Paper>
      {
        openModal.open && (
          <KitBuilderModal
            kit={kit}
            kitItems={kitItems}
            type={type}
            currentUser={currentUser}
            customerNumber={customerNumber}
            openModal={openModal}
            setOpenModal={setOpenModal}
            isUpdatingItems={isUpdatingItems}
            setIsUpdatingItems={setIsUpdatingItems}
            fetchItems={fetchItems}
          />          
        )
      }
    </>
  )
}

export default KitContainer
